import React, { useCallback, useEffect, useState } from "react";
import { financeAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate, formatDateTime } from "../utils/dates";
import Modal from "../components/Modal";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Which sales a customer's balance is actually made of.
 *
 * A payment is never linked to a particular sale — it's just a number
 * against the customer — so "remaining" per purchase here is a read-time
 * simulation done server-side: the oldest sale is always paid off first.
 */
const CreditBreakdown = ({ loading, error, breakdown, money, t }) => {
  if (loading) {
    return (
      <div className="mt-3 text-sm text-gray-400">{t("common.loading")}</div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 text-sm text-red-600">{error}</div>
    );
  }

  if (!breakdown) return null;

  const itemsSummary = (items) =>
    (items || [])
      .map((item) => `${item.quantity}× ${item.product_name}`)
      .join(", ");

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          {t("credit.purchasesTitle")}
        </p>
        <p className="text-xs text-gray-500 mb-2">{t("credit.purchasesHint")}</p>
        <ul className="space-y-2">
          {breakdown.purchases.map((purchase) => (
            <li
              key={purchase.id}
              className="bg-white rounded-lg border border-gray-200 px-3 py-2"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {formatDateTime(purchase.created_at)}
                  {purchase.receipt_number
                    ? ` · #${purchase.receipt_number}`
                    : ""}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {money(purchase.total_amount)}
                </span>
              </div>
              {itemsSummary(purchase.items) && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {itemsSummary(purchase.items)}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
                <span className="text-xs text-gray-500">
                  {purchase.paid_at_sale > 0
                    ? t("credit.paidAtSale", {
                        amount: money(purchase.paid_at_sale),
                      })
                    : ""}
                </span>
                {purchase.remaining > 0 ? (
                  <span className="text-xs font-semibold text-amber-700">
                    {t("credit.remaining", { amount: money(purchase.remaining) })}
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-emerald-700">
                    {t("credit.paidOff")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          {t("credit.paymentsTitle")}
        </p>
        {breakdown.payments.length === 0 ? (
          <p className="text-xs text-gray-500">{t("credit.noPayments")}</p>
        ) : (
          <ul className="space-y-1">
            {breakdown.payments.map((payment) => (
              <li key={payment.id} className="text-xs text-gray-600">
                {formatDateTime(payment.created_at)} ·{" "}
                {payment.note
                  ? t("credit.paymentNote", {
                      amount: money(payment.amount),
                      note: payment.note,
                    })
                  : money(payment.amount)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Who owes this shop money, and recording what they pay back.
 *
 * A balance here is never its own row — it's a customer's credit sales
 * minus their payments, recomputed every time, so it can never drift out of
 * sync with the sales and payments it's built from.
 */
const Credit = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [paying, setPaying] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [paidTo, setPaidTo] = useState("drawer");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await financeAPI.credit();
      setCustomers(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("credit.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPayment = (customer) => {
    setPaying(customer);
    setAmount(String(customer.balance));
    setNote("");
    setPaidTo("drawer");
    setError("");
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const { data } = await financeAPI.recordCreditPayment({
        credit_account_id: paying.credit_account_id,
        amount: parseFloat(amount),
        note: note || undefined,
        paid_to: paidTo,
      });
      setNotice(
        data?.payment?.drawer === "closed"
          ? t("credit.paymentRecordedDrawerClosed", { name: paying.name })
          : t("credit.paymentRecorded", { name: paying.name })
      );
      setPaying(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("credit.paymentFailed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleDetails = async (customer) => {
    if (expanded === customer.credit_account_id) {
      setExpanded(null);
      return;
    }

    setExpanded(customer.credit_account_id);
    setBreakdown(null);
    setBreakdownError("");
    setBreakdownLoading(true);

    try {
      const { data } = await financeAPI.creditBreakdown(
        customer.credit_account_id,
      );
      setBreakdown(data);
    } catch (err) {
      setBreakdownError(
        err.response?.data?.error || t("credit.detailsLoadFailed"),
      );
    } finally {
      setBreakdownLoading(false);
    }
  };

  const totalOwed = customers.reduce((sum, c) => sum + c.balance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("credit.title")}
          </h1>
          <p className="text-gray-600">{t("credit.subtitle")}</p>
        </div>
        {customers.length > 0 && (
          <div className="text-end">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              {t("credit.totalOwed")}
            </p>
            <p className="text-2xl font-bold text-amber-700">
              {money(totalOwed)}
            </p>
          </div>
        )}
      </div>

      {error && !paying && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {customers.length === 0 ? (
          <EmptyState screen="credit" message={t("credit.nobodyOwes")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {customers.map((customer) => (
              <li key={customer.credit_account_id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {customer.name}
                      {/* A tab kept by name alone has no customer account */}
                      {customer.user_id && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          {t("credit.hasAccount")}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {customer.phone && `${customer.phone} · `}
                      {t("credit.lastSale", {
                        day: formatDate(customer.last_sale),
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-amber-700 whitespace-nowrap">
                      {money(customer.balance)}
                    </span>
                    <button
                      onClick={() => toggleDetails(customer)}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold"
                    >
                      {expanded === customer.credit_account_id
                        ? t("credit.hideDetails")
                        : t("credit.details")}
                    </button>
                    {can("credit.edit") && (
                      <button
                        onClick={() => openPayment(customer)}
                        className="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
                      >
                        {t("credit.recordPayment")}
                      </button>
                    )}
                  </div>
                </div>

                {expanded === customer.credit_account_id && (
                  <CreditBreakdown
                    loading={breakdownLoading}
                    error={breakdownError}
                    breakdown={breakdown}
                    money={money}
                    t={t}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {paying && (
        <Modal
          title={t("credit.paymentFrom", { name: paying.name })}
          onClose={() => setPaying(null)}
          maxWidthClass="max-w-md"
        >
          <form onSubmit={submitPayment} className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("credit.currentlyOwed", { amount: money(paying.balance) })}
            </p>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("credit.amount", { currency })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                dir="ltr"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <fieldset className="space-y-1">
              <legend className="text-sm font-medium text-gray-700">
                {t("credit.paidTo")}
              </legend>
              {["drawer", "elsewhere"].map((choice) => (
                <label key={choice} className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="paid_to"
                    value={choice}
                    checked={paidTo === choice}
                    onChange={() => setPaidTo(choice)}
                    className="mt-1"
                  />
                  <span>
                    {t(`credit.paidTo_${choice}`)}
                    <span className="block text-xs text-gray-500">
                      {t(`credit.paidTo_${choice}_hint`)}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("credit.note")}
              </span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              className="w-full px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
            >
              {busy ? t("common.saving") : t("credit.recordPayment")}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Credit;
